import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import * as Ably from 'ably';
import { aiPromptsKeys } from '@/queries/aiPrompts/keys';
import { clientsKeys } from '@/queries/clients/keys';
import { useMe } from '@/queries/me/useMe';
import { notificationsKeys } from '@/queries/notifications/keys';
import { postsKeys } from '@/queries/posts/keys';

export function useAgencyRealtime() {
  const queryClient = useQueryClient();
  const { data: me } = useMe();

  useEffect(() => {
    const agencyId = me?.agency?.id;
    const userId = me?.user?.id;

    if (!agencyId && !userId) {
      return;
    }

    const client = new Ably.Realtime({
      authUrl: '/api/v1/me/ably_auth',
      recover: (_, cb) => {
        console.log('Recovering Ably connection');
        cb(true);
      },
    });

    client.connection.on((stateChange) => {
      console.log('Ably connection state changed', stateChange);
      if (stateChange.current === 'failed') {
        Sentry.captureException(stateChange.reason, { extra: { stateChange } });
      }
    });

    // Agency channel: posts
    const agencyChannel = agencyId ? client.channels.get(`agency-${agencyId}`) : null;
    const handlePostChanged = async (message: Ably.InboundMessage) => {
      const data = message.data as { postId?: string; userId?: string } | undefined;
      const postId = data?.postId;
      const triggeringUserId = data?.userId;
      if (!postId || (triggeringUserId && triggeringUserId === userId)) {
        return;
      }

      try {
        await queryClient.invalidateQueries({ queryKey: postsKeys.item(postId) });
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    const handlePostDeleted = async (message: Ably.InboundMessage) => {
      const data = message.data as { postId?: string; userId?: string } | undefined;
      const postId = data?.postId;
      const triggeringUserId = data?.userId;
      if (!postId || (triggeringUserId && triggeringUserId === userId)) {
        return;
      }

      try {
        await queryClient.invalidateQueries({ queryKey: postsKeys.all });
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    if (agencyChannel) {
      agencyChannel.subscribe('post-changed', handlePostChanged);
      agencyChannel.subscribe('post-deleted', handlePostDeleted);
      try {
        agencyChannel.presence.enter({ userId });
      } catch (error) {
        Sentry.captureException(error);
      }
    }

    // Track last active time to conditionally refresh data on return to foreground
    let lastActiveAt = Date.now();
    let isFocusRefreshRunning = false;

    async function handleFocusRefreshIfNeeded() {
      if (isFocusRefreshRunning) {
        return;
      }

      const now = Date.now();
      const elapsedMs = now - lastActiveAt;
      const twoMinutesMs = 10 * 1000;
      if (elapsedMs < twoMinutesMs) {
        return;
      }

      isFocusRefreshRunning = true;
      try {
        await queryClient.invalidateQueries({ queryKey: clientsKeys.all });
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        isFocusRefreshRunning = false;
      }
    }

    const handleVisibilityChange = () => {
      try {
        if (document.hidden) {
          lastActiveAt = Date.now();
          agencyChannel?.presence.leave();
        } else {
          agencyChannel?.presence.enter({ userId });
          void handleFocusRefreshIfNeeded();
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    };
    window.addEventListener('visibilitychange', handleVisibilityChange);

    // User channel: notifications and AI prompts
    const userChannel = userId ? client.channels.get(`user-${userId}`) : null;
    const handleUserEvent = async (message: Ably.InboundMessage) => {
      const data = (message.data ?? {}) as {
        notificationId?: string | string[];
        notificationIds?: string[];
        aiPromptId?: string | string[];
        aiPromptIds?: string[];
      };

      const notificationIds = [
        ...(Array.isArray(data.notificationIds) ? data.notificationIds : []),
        ...(Array.isArray(data.notificationId)
          ? data.notificationId
          : data.notificationId
            ? [data.notificationId]
            : []),
      ];
      const aiPromptIds = [
        ...(Array.isArray(data.aiPromptIds) ? data.aiPromptIds : []),
        ...(Array.isArray(data.aiPromptId)
          ? data.aiPromptId
          : data.aiPromptId
            ? [data.aiPromptId]
            : []),
      ];

      try {
        if (aiPromptIds.length > 0) {
          void queryClient.invalidateQueries({ queryKey: aiPromptsKeys.all });
        }

        if (notificationIds.length > 0) {
          void queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    if (userChannel) {
      userChannel.subscribe(handleUserEvent);
    }

    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);

      try {
        if (agencyChannel) {
          try {
            agencyChannel.presence.leave();
          } catch (error) {
            Sentry.captureException(error);
          }
          agencyChannel.unsubscribe('post-changed', handlePostChanged);
          agencyChannel.unsubscribe('post-deleted', handlePostDeleted);
        }
      } catch (error) {
        Sentry.captureException(error);
      }
      try {
        if (userChannel) {
          userChannel.unsubscribe(handleUserEvent);
        }
      } catch (error) {
        Sentry.captureException(error);
      }

      try {
        client.connection.off();
        client.close();
      } catch (error) {
        Sentry.captureException(error);
      }
    };
  }, [queryClient, me?.agency?.id, me?.user?.id]);
}
